import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import SVG from 'components/base/SVG';
import { SolidButton } from 'components/Button';
import { withRouter } from 'react-router-dom';
import css from './style.scss';

// Helper function to check if item is a divider
const isDivider = (item) => item && item.divider === true;

// Individual menu item component
const MenuItem = ({ 
  item, 
  onClick, 
  isSelected, 
  closeAll, 
  isSubItem = false, 
  defaultIconSize = 'default',
  expandedSubItems = {},
  onToggleSubItem = null,
  hoverMessage = "Select Item(s) First",
  onTooltipChange = null,
  isRightAligned = false,
  dropdownRect = null
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const itemRef = useRef(null);

  if (isDivider(item)) {
    return <div className={classNames(css.divider, { [css.campaignDivider]: item.campaign })} />;
  }

  // Use item's iconStyle if provided, otherwise use default
  const iconSize = item.iconSize || defaultIconSize;
  const iconStyle = item.iconStyle || '';

  const handleMouseEnter = (e) => {
    if (item.disabled && itemRef.current && onTooltipChange) {
      const rect = itemRef.current.getBoundingClientRect();
      const tooltipWidth = 156; // Approximate tooltip width
      
      let x, y;
      
      if (isRightAligned) {
        // Position to the LEFT of the item for right-aligned dropdowns
        x = rect.left - tooltipWidth - 20;
        // If tooltip would go off screen, position it to the right instead
        if (x < 10) {
          x = rect.right + 12;
        }
      } else {
        // Position to the RIGHT of the item for left-aligned dropdowns
        x = rect.right + 20;
        // If tooltip would go off screen, position it to the left instead
        if (x + tooltipWidth > window.innerWidth - 10) {
          x = rect.left - tooltipWidth - 12;
        }
      }
      
      // Center vertically
      y = rect.top + (rect.height / 2) - 20;
      
      // Ensure tooltip stays within viewport vertically
      if (y < 10) y = 10;
      if (y + 40 > window.innerHeight - 10) y = window.innerHeight - 50;
      
      onTooltipChange({
        visible: true,
        x: x,
        y: y,
        message: hoverMessage,
        isRightAligned: isRightAligned
      });
    }
  };

  const handleMouseLeave = () => {
    if (item.disabled && onTooltipChange) {
      onTooltipChange({
        visible: false,
        x: 0,
        y: 0,
        message: '',
        isRightAligned: false
      });
    }
  };

  // Check if this has sub-items (like "Add to List")
  if (item.subItems && item.subItems.length > 0) {
    // Use the parent's expanded state
    const isExpanded = expandedSubItems[item.value || item.key || item.label] || false;
    
    const handleToggle = (e) => {
      e.stopPropagation();
      if (!item.disabled && onToggleSubItem) {
        onToggleSubItem(item.value || item.key || item.label);
      }
    };

    return (
      <div className={css.subMenuWrapper}>
        <div 
          ref={itemRef}
          className={classNames(css.item, css.hasSubMenu, { 
            [css.disabled]: item.disabled,
            [css.expanded]: isExpanded
          })} 
          onClick={handleToggle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {item.icon && (
            <span className={classNames(css.itemIcon, {
              [css.iconSizeSmall]: iconSize === 'small',
              [css.iconSizeDefault]: iconSize === 'default',
              [css.iconSizeLarge]: iconSize === 'large',
              [css.iconSizeLargeSecondary]: iconStyle === 'iconSizeLargeSecondary',
              [css.iconSizeCustom]: iconSize === 'custom'
            })}>
              {typeof item.icon === 'string' ? <SVG icon={item.icon} /> : item.icon}
            </span>
          )}
          <div className={css.itemContent}>
            <span className={css.itemLabel}>{item.label}</span>
            {item.subtitle && <span className={css.itemSubtitle}>{item.subtitle}</span>}
          </div>
          <SVG icon="iconCaretDown" className={classNames(css.chevron, { [css.rotated]: isExpanded })} />
        </div>
        {isExpanded && (
          <div className={css.subItems}>
            {item.subItems.map((subItem, idx) => (
              <MenuItem 
                key={subItem.value || idx}
                item={subItem}
                onClick={onClick}
                isSelected={isSelected}
                closeAll={closeAll}
                isSubItem={true}
                defaultIconSize={defaultIconSize}
                expandedSubItems={expandedSubItems}
                onToggleSubItem={onToggleSubItem}
                hoverMessage={hoverMessage}
                onTooltipChange={onTooltipChange}
                isRightAligned={isRightAligned}
                dropdownRect={dropdownRect}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div 
      ref={itemRef}
      className={classNames(css.item, { 
        [css.selected]: isSelected,
        [css.disabled]: item.disabled,
        [css.subItem]: isSubItem,
        [css.hasSubtitle]: item.subtitle
      })} 
      onClick={() => !item.disabled && onClick(item)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {item.icon && (
        <span className={classNames(css.itemIcon, {
          [css.iconSizeSmall]: iconSize === 'small',
          [css.iconSizeDefault]: iconSize === 'default',
          [css.iconSizeLarge]: iconSize === 'large',
          [css.iconSizeLargeSecondary]: iconStyle === 'iconSizeLargeSecondary',
          [css.iconSizeCustom]: iconSize === 'custom'
        })}>
          {typeof item.icon === 'string' ? <SVG icon={item.icon} /> : item.icon}
        </span>
      )}
      <div className={css.itemContent}>
        <span className={css.itemLabel}>{item.label}</span>
        {item.subtitle && <span className={css.itemSubtitle}>{item.subtitle}</span>}
      </div>
      {item.badge && <span className={css.badge}>{item.badge}</span>}
      {item.shortcut && <span className={css.shortcut}>{item.shortcut}</span>}
    </div>
  );
};

const ActionDropdownMenu = ({ 
  text, 
  icon, 
  loading, 
  items, 
  direction = 'bottom',
  caption, 
  title, 
  selection, 
  leftAnchored = false,
  dropdownClassName,
  dropdownCustomStyle,
  dropdownStyle,
  onSelect,
  history,
  closeDropdown,
  isOpen,
  onClick,
  disabled,
  hoverMessage = "Select Item(s) First",
  showHoverState = false,
  minWidth = 273,
  iconSize = 'default',
  trigger,
  TriggerComponent = SolidButton
}) => {
  const [open, setOpen] = useState(isOpen || false);
  const [expandedSubItems, setExpandedSubItems] = useState({});
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    message: '',
    isRightAligned: false
  });
  const dropdownRef = useRef(null);
  const [dropdownRect, setDropdownRect] = useState(null);

  useEffect(() => {
    if (!open) {
      setExpandedSubItems({});
      setTooltip({ visible: false, x: 0, y: 0, message: '', isRightAligned: false });
    }
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
        setExpandedSubItems({});
        setTooltip({ visible: false, x: 0, y: 0, message: '', isRightAligned: false });
        if (closeDropdown) closeDropdown();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      // Get dropdown position
      if (dropdownRef.current) {
        const rect = dropdownRef.current.getBoundingClientRect();
        setDropdownRect(rect);
      }
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open, closeDropdown]);

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const newOpenState = !open;
    setOpen(newOpenState);
    
    // Reset sub-items when toggling
    if (newOpenState) {
      setExpandedSubItems({});
      setTooltip({ visible: false, x: 0, y: 0, message: '', isRightAligned: false });
    }
    
    if (onClick) onClick(e);
    
    if (newOpenState && closeDropdown) {
      closeDropdown();
    }
  };

  const handleItemClick = (item) => {
    setOpen(false);
    setExpandedSubItems({});
    setTooltip({ visible: false, x: 0, y: 0, message: '', isRightAligned: false });
    
    if (closeDropdown) closeDropdown();
    
    const onSel = item.onSelect || onSelect;
    if (onSel) onSel(item);
    
    if (item.url && history) history.push(item.url);
  };

  const handleToggleSubItem = (itemId) => {
    setExpandedSubItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleTooltipChange = (tooltipData) => {
    setTooltip(tooltipData);
  };

  const validItems = items.filter(item => item !== null);

  const dropdownStyleObj = {
    ...dropdownCustomStyle,
    ...dropdownStyle,
    ...(minWidth !== 273 ? { minWidth: typeof minWidth === 'number' ? `${minWidth}px` : minWidth } : {})
  };

  // Check if dropdown is right-aligned
  const isRightAligned = !leftAnchored;

  return (
    <div 
      ref={dropdownRef}
      className={classNames(css.dropdownMenu, { 
        [css.open]: open, 
        [css.iconMode]: !!icon, 
        [css.left]: leftAnchored,
        [css.right]: !leftAnchored,
      }, dropdownClassName)} 
      name="action-dropdown-btn" 
      id={text}
    >
      <div onClick={handleToggle} className={css.dropdownTrigger}>
        {trigger ? (
          trigger
        ) : text ? (
          <TriggerComponent
            text={text}
            className={css.importButton1}
            icon={icon}
            loading={loading}
          />
        ) : (
          <SVG icon={icon} className={css.icon} title={title} />
        )}
      </div>
      
      <div className={css.dropdownWrapper}>
        <div 
          className={classNames(css.dropdown, { 
            [css.top]: direction === 'top'
          })}
          style={dropdownStyleObj}
        >
          {caption && <div className={css.caption}>{caption}</div>}
          
          <div className={css.itemsContainer}>
            {validItems.map((item, index) => (
              <MenuItem 
                key={item.value || item.key || index}
                item={item}
                onClick={handleItemClick}
                isSelected={'value' in item && item.value === selection}
                closeAll={() => {
                  setOpen(false);
                  setExpandedSubItems({});
                  setTooltip({ visible: false, x: 0, y: 0, message: '', isRightAligned: false });
                }}
                defaultIconSize={iconSize}
                expandedSubItems={expandedSubItems}
                onToggleSubItem={handleToggleSubItem}
                hoverMessage={hoverMessage}
                onTooltipChange={handleTooltipChange}
                isRightAligned={isRightAligned}
                dropdownRect={dropdownRect}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Tooltip rendered at the root level using fixed positioning */}
      {tooltip.visible && (
        <div 
          className={classNames(css.globalTooltip, {
            [css.rightAlignedTooltip]: tooltip.isRightAligned
          })}
          style={{
            position: 'fixed',
            left: tooltip.x + 'px',
            top: tooltip.y + 'px',
            zIndex: 999999,
            pointerEvents: 'none',
            opacity: 1
          }}
        >
          <div className={css.tooltipContent}>
            <span className={css.tooltipText}>{tooltip.message}</span>
            <div className={classNames(css.tooltipArrow, {
              [css.arrowRight]: tooltip.isRightAligned
            })} />
          </div>
        </div>
      )}
    </div>
  );
};

ActionDropdownMenu.propTypes = {
  dropdownClassName: PropTypes.string,
  placeholder: PropTypes.string,
  direction: PropTypes.oneOf(['top', 'bottom']),
  caption: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  url: PropTypes.string,
  title: PropTypes.string,
  selection: PropTypes.any,
  onSelect: PropTypes.func,
  leftAnchored: PropTypes.bool,
  text: PropTypes.string,
  loading: PropTypes.bool,
  isOpen: PropTypes.bool,
  closeDropdown: PropTypes.func,
  onClick: PropTypes.func,
  history: PropTypes.object,
  disabled: PropTypes.bool,
  hoverMessage: PropTypes.string,
  showHoverState: PropTypes.bool,
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  iconSize: PropTypes.oneOf(['small', 'default', 'large']),
  dropdownStyle: PropTypes.object, 
  dropdownCustomStyle: PropTypes.object,
  trigger: PropTypes.node,
  TriggerComponent: PropTypes.elementType,
  items: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.shape({ divider: PropTypes.bool })
  ])).isRequired,
};

ActionDropdownMenu.defaultProps = {
  direction: 'bottom',
  leftAnchored: false,
  loading: false,
  disabled: false,
  hoverMessage: "Select Item(s) First",
  showHoverState: false,
  minWidth: 273,
  iconSize: 'default',
  dropdownStyle: {},
  dropdownCustomStyle: {},
  TriggerComponent: SolidButton
};

export default withRouter(ActionDropdownMenu);